import React, { ChangeEvent, MouseEvent } from 'react'
import clsx from 'clsx'
import range from 'lodash.range'
import uniqueid from 'lodash.uniqueid'
import { Pagination as PaginationStyles } from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import { mockButtonClickEvent } from '../../lib/utils'
import ComboBox from '../ComboBox'
import IconButton from '../IconButton'
import Select from '../Select'
import { CommonProps } from '../helperTypes'

type Props = {
  /** The total number of rows. */
  count: number
  /** Customizes label of goto select */
  gotoLabel?: string
  /** The legend of the pagination. */
  legend?: string
  /** The callback fired when the user clicks a page link */
  onPageChange: (event: MouseEvent<HTMLButtonElement>, page: number) => void
  /** The callback fired when the number of rows per page is changed */
  onRowsPerPageChange?: (event: ChangeEvent<HTMLSelectElement>, value: any) => void
  /** The one-based index of the current page. */
  page: number
  /** The number of rows per page. */
  rowsPerPage: number
  /** Customizes label to select the rows per page */
  rowsPerPageLabel?: string
  /** Customizes the options of the rows per page select field. */
  rowsPerPageOptions?: number[]
  /** If true, the goto page select will be enabled */
  showGoto?: boolean
  /** If true, the rows per page select will be enabled */
  showRowsPerPage?: boolean
}

export type PaginationProps = Props & CommonProps

const ROWS_PER_PAGE_OPTIONS_DEFAULT = [5, 10, 25, 100]

const useStyles = createUseStyles(PaginationStyles, 'pagination')

const Pagination = ({
  className,
  count,
  legend,
  onPageChange,
  onRowsPerPageChange,
  showGoto,
  showRowsPerPage,
  gotoLabel = 'Go to page',
  page,
  rowsPerPage,
  rowsPerPageLabel = 'Entries per page',
  rowsPerPageOptions = ROWS_PER_PAGE_OPTIONS_DEFAULT,
  ...rest
}: PaginationProps) => {
  const classes = useStyles()
  const [gotoPageValue, setGotoPageValue] = React.useState('')

  const pageCount = Math.ceil(count / rowsPerPage)
  const showEllipsis = pageCount > 7
  const hasLeftEllipsis = showEllipsis && page > 4
  const hasRightEllipsis = showEllipsis && page < pageCount - 3
  // compute the range of page numbers
  // to display between first and last pages
  // - min range value (included in range)
  let startPageMin = page - 5
  if (showEllipsis) {
    startPageMin = hasLeftEllipsis ? page - 1 : page - 2
  }
  const startPage = Math.max(2, Math.min(pageCount - 4, startPageMin))
  // - max range value (not included in range)
  let endPageMax = pageCount
  if (showEllipsis) {
    endPageMax = hasRightEllipsis ? page + 2 : page + 3
  }
  const endPage = Math.min(pageCount, Math.max(6, endPageMax))
  // page range from start to end-1
  const pageRange = endPage > 2 ? range(startPage, endPage) : []

  const gotoPageRange = React.useMemo(() => {
    return [...Array(pageCount).keys()].map((p: number) => (p + 1).toString())
  }, [pageCount])

  const handleRowsPerPageChange = React.useCallback(
    (evt: ChangeEvent<HTMLSelectElement>, val: any) => {
      setGotoPageValue('')
      onRowsPerPageChange && onRowsPerPageChange(evt, val)
    },
    [onRowsPerPageChange],
  )

  const handlePageChange = React.useCallback(
    (evt: MouseEvent<HTMLButtonElement>, val: number, gotoPageVal: string = '') => {
      setGotoPageValue(gotoPageVal)
      onPageChange(evt, val)
    },
    [onPageChange],
  )

  return (
    <div className={clsx(classes.root, className)} {...rest}>
      {legend && <div className={classes.legend}>{legend}</div>}
      {(showRowsPerPage || onRowsPerPageChange) && (
        <div className={clsx(classes.side, classes.side_left)}>
          <span className={classes.side_label}>{rowsPerPageLabel}</span>
          <Select value={rowsPerPage} onChange={handleRowsPerPageChange} className={classes.side_input}>
            {rowsPerPageOptions.map((val) => (
              <option key={val} value={val}>
                {val}
              </option>
            ))}
          </Select>
        </div>
      )}
      <nav className={classes.nav} aria-label='Pagination'>
        <IconButton
          className={clsx(classes.item, classes.item_prev)}
          disabled={page === 1}
          aria-label='Previous page'
          onClick={(event) => handlePageChange(event, page - 1)}
        />
        <PaginationItem
          page={1}
          active={page === 1}
          onClick={(event: MouseEvent<HTMLButtonElement>) => handlePageChange(event, 1)}
        />
        {hasLeftEllipsis && <PaginationItemEllipsis />}
        {pageRange.map((p: number) => (
          <PaginationItem
            key={uniqueid('page-')}
            page={p}
            active={p === page}
            onClick={(event: MouseEvent<HTMLButtonElement>) => handlePageChange(event, p)}
          />
        ))}
        {hasRightEllipsis && <PaginationItemEllipsis />}
        {pageCount > 1 && (
          <PaginationItem
            page={pageCount}
            active={page === pageCount}
            onClick={(event: MouseEvent<HTMLButtonElement>) => handlePageChange(event, pageCount)}
          />
        )}
        <IconButton
          className={clsx(classes.item, classes.item_next)}
          disabled={page === pageCount}
          aria-label='Next page'
          onClick={(event) => handlePageChange(event, page + 1)}
        />
      </nav>
      {showGoto && (
        <div className={clsx(classes.side, classes.side_right)}>
          <span className={classes.side_label}>{gotoLabel}</span>
          <ComboBox
            className={classes.side_right_width}
            hideClear
            options={gotoPageRange}
            value={gotoPageValue}
            onChange={(value: any) => {
              if (!value) return
              // we need to mock mouseevent so we don't introduce breaking change in API
              handlePageChange(mockButtonClickEvent(), Number(value), value)
            }}
          />
        </div>
      )}
    </div>
  )
}

type ItemProps = {
  /**
   * Flag for current page item
   */
  active?: boolean
  /**
   * The one-based index of the current page
   */
  page: number
}

export type PaginationItemProps = ItemProps & CommonProps

const PaginationItemEllipsis = () => {
  const classes = useStyles()
  return (
    <IconButton className={classes.item} disabled>
      &hellip;
    </IconButton>
  )
}

export const PaginationItem = ({ active, page, ...rest }: PaginationItemProps) => {
  const classes = useStyles()
  return (
    <button
      type='button'
      className={clsx(classes.item, active && classes.item_active)}
      aria-current={active ? 'page' : undefined}
      aria-label={`Page ${page}`}
      {...rest}
    >
      {page}
    </button>
  )
}

export default Pagination
